import { ConferenceCreatedByOtherEvent, ConferenceCreatedByYouEvent, ConferenceMember, ConferenceType, GroupStatusEvent, HistoryMessageItem, useIM } from '@wui/im'
import { defineStore } from 'pinia'
import { ref } from 'vue'

import { onReplaceableCallReceive } from '@/entities/webrtc'

import { PSTNGUEST } from '@/shared/utils/constants'
import { fromPServedUser } from '@/shared/utils/uri'

const REPLACEABLE_CALL_ID_RESOLVE_TIMEOUT_MS = 5_000
const REPLACE_SUCCEED_RESOLVE_TIMEOUT_MS = 25_000

interface ConferenceFromDialogTxResolver {
  inviteReceived: () => void,
  dialogCreated: () => void,
  replaceSucceed: (existedParticipantNumber: string) => boolean,
}

export type ConferenceFromDialogTxResult = [
  PromiseSettledResult<HistoryMessageItem<ConferenceCreatedByYouEvent>>,
  PromiseSettledResult<boolean>,
  PromiseSettledResult<boolean>,
  PromiseSettledResult<boolean>,
]

export type ConferenceFromDialogState =
  | 'initial'
  | 'emptyCallIdError'
  | 'loading'
  | 'success'
  | 'commandAcceptedError'
  | 'inviteReceivedAndDialogCreatedError'
  | 'inviteReceivedError'
  | 'dialogCreatedError'
  | 'replaceSucceedError'

export const useCreatedFromDialogCallSessionsStore = defineStore('created-from-dialog-call-sessions-store', () => {

  const { createConference, onConferenceCreated, onConferenceUpdated } = useIM()
  const txMap = ref<Map<string, ConferenceFromDialogTxResolver>>(new Map)
  const callIdByPServed = ref<Map<string, string>>(new Map)

  const create = async (
    chatName: string,
    newParticipants: Array<ConferenceMember>,
    replaceableCallId: string,
    existedParticipant: ConferenceMember,
  ): Promise<ConferenceFromDialogTxResult> => {
    const txItem: Partial<ConferenceFromDialogTxResolver> = {}
    const inviteReceived = new Promise<boolean>((resolve, reject) => {
      txItem.inviteReceived  = () => resolve(true)
      setTimeout(() => {
        const resolveFn = txMap.value.get(replaceableCallId)
        if (resolveFn) {
          reject(`Timeout ${REPLACEABLE_CALL_ID_RESOLVE_TIMEOUT_MS} ms reached`)
        }
      }, REPLACEABLE_CALL_ID_RESOLVE_TIMEOUT_MS)
    })
    const dialogCreated = new Promise<boolean>((resolve, reject) => {
      txItem.dialogCreated = () => resolve(true)
      setTimeout(() => {
        const resolveFn = txMap.value.get(replaceableCallId)
        if (resolveFn) {
          reject(`Timeout ${REPLACEABLE_CALL_ID_RESOLVE_TIMEOUT_MS} ms reached`)
        }
      }, REPLACEABLE_CALL_ID_RESOLVE_TIMEOUT_MS)
    })
    const replaceSucceed = new Promise<boolean>((resolve, reject) => {
      txItem.replaceSucceed  = (participantNumber: string) => {
        if (newParticipants.find(({ number }) => number === participantNumber)) {
          resolve(true)
          return true
        }
        return false
      }
      setTimeout(() => {
        const resolveFn = txMap.value.get(replaceableCallId)
        if (resolveFn) {
          reject(`Timeout ${REPLACE_SUCCEED_RESOLVE_TIMEOUT_MS} ms reached`)
        }
      }, REPLACE_SUCCEED_RESOLVE_TIMEOUT_MS)
    })
    txMap.value.set(replaceableCallId, txItem as ConferenceFromDialogTxResolver)
    const commandAccepted = createConference({
      mode: ConferenceType.FREE_FOR_ALL,
      name: chatName,
      subscribers: [...newParticipants, existedParticipant],
      replaceableCallId,
    }).then((commandAccepted) => {
      callIdByPServed.value.set(commandAccepted.item.pServed, replaceableCallId)
      return commandAccepted
    })

    return Promise.allSettled([
      commandAccepted,
      dialogCreated,
      inviteReceived,
      replaceSucceed,
    ]).finally(() => {
      // no simultaneous create by dialogs allowed, clear all
      callIdByPServed.value.clear()
      txMap.value.delete(replaceableCallId)
    })
  }

  const hasCallId = (callId: string) => {
    return txMap.value.has(callId)
  }

  onConferenceCreated((event: ConferenceCreatedByOtherEvent) => {
    const { replaceableCallId } = event
    if (replaceableCallId) {
      const resolveFn = txMap.value.get(replaceableCallId)
      if (resolveFn) {
        resolveFn.dialogCreated()
      }
    }
  })

  onReplaceableCallReceive((callId: string) => {
    const resolveFn = txMap.value.get(callId)
    if (resolveFn) {
      resolveFn.inviteReceived()
    }
  })

  onConferenceUpdated((event: GroupStatusEvent) => {
    const { pServed, extendedParams: { contacts = [] }} = event
    const callId = callIdByPServed.value.get(pServed)
    if (callId) {
      const resolveFn = txMap.value.get(callId)
      if (resolveFn) {
        contacts.filter(({ state }) => {
          return state > 1
        }).forEach(({ id, type }) => {
          if (type === 'pstnGuest') {
            resolveFn.replaceSucceed(`${PSTNGUEST}-${id}`)
          } else {
            resolveFn.replaceSucceed(fromPServedUser(id))
          }
        })
      }
    }
  })

  return {
    create,
    hasCallId,
  }
})
